import {
  Button,
  Chip,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  Box,
} from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';
import { formatCurrencyFloat } from 'shared/utils/numberFormatters';
import { CompplexCompStatusColor, CompplexCompStatusLabel } from 'components/underwriting/constants';
import { useMemo } from 'react';
import { ResidentialRentComp, ResidentialRentRollUnit } from 'types/underwriting/income-approach';
import { calculateMonthlyRentSummary } from '../helpers';

type RentCompsTableProps = {
  name: string;
  subjectUnits: ResidentialRentRollUnit[];
  comps: ResidentialRentComp[];
  removeComp: (comp: any) => void;
  showViewModal: (comp: any) => void;
};

export const RentCompsTable: React.FC<RentCompsTableProps> = ({
  name,
  subjectUnits,
  comps,
  showViewModal,
  removeComp,
}) => {
  const rentSummary = useMemo(() => calculateMonthlyRentSummary(subjectUnits, comps), [subjectUnits, comps]);

  return (
    <Paper sx={{ padding: '16px' }}>
      <Grid>
        <Typography variant="h5">{name}</Typography>
      </Grid>
      <Grid sx={{ marginTop: '16px', marginBottom: '16px' }}>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Summary</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Min</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Max</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Average</Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Subject Units</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.subjectUnits.min)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.subjectUnits.max)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.subjectUnits.average)}</Typography>
          </Grid>
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={3}>
            <Typography variant="subtitle1">Comps</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.comps.min)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.comps.max)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography>{formatCurrencyFloat(rentSummary.comps.average)}</Typography>
          </Grid>
        </Grid>
      </Grid>
      <Grid>
        <TableContainer>
          <Table size="medium" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center" width={300}>
                  Address
                </TableCell>
                <TableCell align="center" width={120}>
                  Unit Number
                </TableCell>
                <TableCell align="center" width={120}>
                  Status
                </TableCell>
                <TableCell align="right" width={120}>
                  Bedrooms
                </TableCell>
                <TableCell align="right" width={120}>
                  Bathrooms
                </TableCell>
                <TableCell align="right" width={150}>
                  Monthly Rent
                </TableCell>
                <TableCell align="right" width={120}>
                  SF
                </TableCell>
                <TableCell align="right" width={120}>
                  Rent/SF
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {comps.map((comp) => (
                <TableRow key={comp.leaseId}>
                  <TableCell align="center" width={300}>
                    {comp.address}
                    {comp.leaseId && !comp.isLatestVersion && (
                      <Tooltip placement="top" title="Outdated">
                        <Box component="span" sx={{ position: 'relative', top: 4, left: 8 }}>
                          <ErrorOutline color="error" fontSize="small" />
                        </Box>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell align="center" width={120}>
                    {comp.unitNumber}
                  </TableCell>
                  <TableCell align="center" width={120}>
                    <Chip
                      label={CompplexCompStatusLabel[comp.status] || 'Draft'}
                      sx={{ background: CompplexCompStatusColor[comp.status] }}
                    />
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {comp.bedrooms}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {comp.bathrooms || ''}
                  </TableCell>
                  <TableCell align="right" width={150}>
                    {formatCurrencyFloat(comp.monthlyRent)}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {comp.squareFeet || ''}
                  </TableCell>
                  <TableCell align="right" width={120}>
                    {comp.annualRentPerSF ? formatCurrencyFloat(comp.annualRentPerSF) : ''}
                  </TableCell>
                  <TableCell align="center">
                    <Button variant="outlined" onClick={() => showViewModal(comp)} sx={{ margin: '0px 8px' }}>
                      DETAILS
                    </Button>
                    <Button variant="contained" onClick={() => removeComp(comp)} sx={{ margin: '0px 8px' }}>
                      REMOVE
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Paper>
  );
};
